import React, { useEffect, useState } from 'react';
import Imgix from 'react-imgix';
import { useInView } from 'react-intersection-observer';
import styled, { createGlobalStyle } from 'styled-components';
import ShowErrors from '../../../../../common/ShowErrors';
import {
  formatGTMDataForProductDetails,
  gtmTrackEvent
} from '@/utils/gtmTrackEvent';
import {
  checkIsOutdoorRug,
  checkIsPad,
  checkIsRug,
  checkIsThrowPillow,
  getFoundationSlug,
  getImgixUrl,
  showThePerfectMatchModule
} from '@/utils/helper';
import { pinterestTrackEvent } from '@/utils/pinterestTrackEvent';
import {
  formatSegmentDataForProductClickedEvent,
  segmentTrackEvent
} from '@/utils/segmentTracking';
import Assembly from './assembly/Assembly';
import Bundle from './bundle/Bundle';
import Cart from './cart/Cart';
import CasterLegs from './casterLegs/CasterLegs';
import CompleteTheLook from './completeTheLook/CompleteTheLook';
import DetailsAndCare from './detailsAndCare/DetailsAndCare';
import Dimensions from './dimensions/Dimensions';
import ExtendWarranty from './extendWarranty/ExtendWarranty';
import Fabric from './fabric/Fabric';
import FabricDescription from './fabricDescription/FabricDescription';
import Firmness from './firmness/Firmness';
import ImageSliderMobile from './ImageSlider/ImageSliderMobile';
import Legs from './legs/Legs';
import Lining from './lining/Lining';
import Orientation from './orientation/Orientation';
import PillowInserts from './pillowInserts/PillowInserts';
import ProductDescription from './productDescription/ProductDescription';
import ProductInfo from './productInfo/ProductInfo';
import SetOfTwo from './setOfTwo/SetOfTwo';
import Size, {Sizes} from './size/Size';
import ThePerfectMatch from './thePerfectMatch/ThePerfectMatch';
import ValueProposition from './valueProposition/ValueProposition';
import WallpaperCalculator from './wallpaperCalculator/WallpaperCalculator';
import WallpaperType from './wallpaperType/WallpaperType';
import YouMayAlsoNeed from './youMayAlsoNeed/YouMayAlsoNeed';
import {
  isFabricQuickShip
} from '@/common/QuickShip';
import CurtainLength
  from "@/components/customers/product/productBody/sidebar/curtainLength/CurtainLength";
import ConstructionType
  from "@/components/customers/product/productBody/sidebar/constructionType/ConstructionType";

const Wrapper = styled.div`
  max-width: 420px;
  width: 420px;
  min-width: 420px;
  border-left: 1px solid #d8d8d8;
  margin-left: auto;

  .error-box-element {
    margin: 25px;
  }

  @media (max-width: 992px) {
    max-width: 100%;
    width: 100%;
    min-width: 100%;
  }

  .quick-ship-switch {
    justify-content: center;
  }
`;

const GlobalStyle = createGlobalStyle`
  .nav-banner-container {
    transition: all 0.4s;
    position: ${({ open }) => (open ? 'absolute' : 'unset')};
    opacity: ${({ open }) => (open ? '0' : '1')};
    z-index: -1;
  }
`;

function Sidebar({
  handleToggleSideCart,
  handleShowFabric,
  hoveredFabric,
  setHoveredFabric,
  isMobile,
  productVar,
  setProductVar,
  dataFabrics,
  sliderImages,
  loading: loadingOption,
  featuredFabrics,
  loadingFabrics: loadingFabricSidebarFabrics,
  setChangeFeaturedFabrics,
  productData,
  setShowHowToWallpaper,
  errorFindProduct,
  goToDimensionsImageSlide,
  shippingFilter,
  setShippingFilter
}) {
  const [extendPlanSelected, setExtendPlanSelected] = useState(null);

  const [cartQuantity, setCartQuantity] = useState(1);
  useEffect(() => {
    pinterestTrackEvent('track', 'pagevisit', {
      product_id: productData.id
    });
    segmentTrackEvent(
      'Product Clicked',
      formatSegmentDataForProductClickedEvent(productData)
    );
    gtmTrackEvent(formatGTMDataForProductDetails(productData));
  }, [productData.id]);

  const [ref, inView] = useInView({
    threshold: 0,
    rootMargin: '-60px 0px 1000px 0px'
  });

  const isWallpaper = productData.frame?.type.id == 4;

  const isRug = checkIsRug(productData?.frame?.name);
  const isPad = checkIsPad(productData?.frame?.name);
  const isOutdoorRug = checkIsOutdoorRug(productData?.frame?.name);

  const foundationSlug = getFoundationSlug(
    productData?.frame?.id,
    productData?.frame?.size?.id
  );
  const isExtendVersion = window?.variantForExtend == 1;
  const isColorTreatment = productData?.fabric?.treatment.id == 2;

  const isBedroomSets =
    productData?.frame?.primarySubcategory.category_id == 39;

  const isCategoryOneOrSix = [1, 6].includes(
    parseInt(productData?.frame?.primarySubcategory?.category_id)
  );

  const isCurtains = (productData.frame.name.includes('Curtains'));

  return (
    <Wrapper>
      {errorFindProduct && !loadingOption && (
        <ShowErrors scroll className="error-box" error={errorFindProduct} />
      )}
      <div className="hide-on-desktop">
        <ProductInfo
          isMobile={isMobile}
          productData={productData}
          name={productData.name}
          price={productData.price}
          fullPrice={productData.full_price}
        />
      </div>

      <div className="image-slider-mobile hide-on-desktop">
        <ImageSliderMobile
          sliderImages={sliderImages}
          loading={loadingOption}
          inView={inView}
          productData={productData}
        />
      </div>
      {isMobile && <div className="observable hide-on-desktop" ref={ref} />}
      <div className="hide-on-mobile">
        <ProductInfo
          isMobile={isMobile}
          productData={productData}
          name={productData.name}
          price={productData.price}
          fullPrice={productData.full_price}
        />
      </div>

      {!!productData.bundle_products?.length && (
        <Bundle
          productData={productData}
          setChangeFeaturedFabrics={setChangeFeaturedFabrics}
        />
      )}

      {hasFabrics(productData, featuredFabrics) && (
        <Fabric
          dataFabrics={dataFabrics}
          productData={productData}
          setProductVar={setProductVar}
          featuredFabrics={featuredFabrics.getFeaturedProductFabrics}
          fabricId={productData.fabric.id}
          handleShowFabric={handleShowFabric}
          setHoveredFabric={setHoveredFabric}
          loading={loadingFabricSidebarFabrics}
          shippingFilter={shippingFilter}
        />
      )}

      {isExtendVersion && (
        <ExtendWarranty
          productData={productData}
          extendPlanSelected={extendPlanSelected}
          setExtendPlanSelected={setExtendPlanSelected}
        />
      )}

      {productData?.availableWallpaperTypes?.length ? (
        <WallpaperType
          setChangeFeaturedFabrics={setChangeFeaturedFabrics}
          productData={productData}
          setProductVar={setProductVar}
          wallpaperTypeId={
            productData?.availableWallpaperTypes?.includes(
              productVar?.frame?.wallpaper_type
            )
              ? productVar?.frame?.wallpaper_type
              : productData?.frame?.wallpaper_type
          }
        />
      ) : (
        ''
      )}
      {isWallpaper && (
        <WallpaperCalculator
          productData={productData}
          setCartQuantity={setCartQuantity}
          setShowHowToWallpaper={setShowHowToWallpaper}
        />
      )}

      {productData.frame.size && !isCurtains && (
        <Size
          setChangeFeaturedFabrics={setChangeFeaturedFabrics}
          productData={productData}
          setProductVar={setProductVar}
          sizeId={productVar.size_id || productData.frame.size.id}
          availableSizes={productData.availableSizes}
          shippingFilter={shippingFilter}
        />
      )}

      {productData.pillowInsert && (
        <PillowInserts
          productData={productData}
          setProductVar={setProductVar}
          pillowInsertId={
            productVar.pillow_insert_id || productData.pillowInsert.id
          }
          pillowInserts={productData.frame.pillowInserts}
        />
      )}

      {productData.constructionType && (
      <ConstructionType
        productData={productData}
        setProductVar={setProductVar}
        constructionTypeId={productVar.constructionType_id || productData.constructionType.id}
        constructionTypes={productData.frame.constructionTypes}
      />
      )}

      {productData.frame.size && isCurtains && (
        <CurtainLength
          setChangeFeaturedFabrics={setChangeFeaturedFabrics}
          productData={productData}
          setProductVar={setProductVar}
          sizeId={productVar.size_id || productData.frame.size.id}
          availableSizes={productData.availableSizes}
          shippingFilter={shippingFilter}
        />
      )}

      {productData.lining && (
        <Lining
          productData={productData}
          setProductVar={setProductVar}
          liningId={productVar.lining_id || productData.lining.id}
          linings={productData.frame.linings}
        />
      )}

      {productData.frame.firmness && (
        <Firmness
          productData={productData}
          setProductVar={setProductVar}
          frameId={productVar.frame_id || productData.frame.id}
          firmnessId={productVar.firmness_id || productData.frame.firmness.id}
          availableFirmnesses={productData.availableFirmnesses}
        />
      )}

      {productData.leg && (
        <Legs
          productData={productData}
          setProductVar={setProductVar}
          legId={productVar.leg_id || productData.leg.id}
          legs={productData.frame.legs}
        />
      )}
      {productData?.frame?.has_assembly_options == 1 && (
        <Assembly
          productData={productData}
          setProductVar={setProductVar}
          assemblyId={
            [0, 1].includes(parseInt(productVar.is_assembled))
              ? productVar.is_assembled
              : productData.is_assembled
          }
        />
      )}
      {productData?.frame?.has_set_of_two == 1 && (
        <SetOfTwo
          productData={productData}
          setProductVar={setProductVar}
          supplierQuantity={
            [1, 2].includes(parseInt(productVar.supplier_quantity))
              ? productVar.supplier_quantity
              : productData.supplier_quantity
          }
        />
      )}
      {productData.casterLeg && (
        <CasterLegs
          productData={productData}
          setProductVar={setProductVar}
          casterLegId={productVar.caster_leg_id || productData.casterLeg.id}
          casterLegs={productData.frame.casterLegs}
        />
      )}
      {productData.frame.orientation && (
        <Orientation
          productData={productData}
          setProductVar={setProductVar}
          frameId={productData.frame.id}
          orientationId={
            productVar.orientation || productData.frame.orientation
          }
        />
      )}
      <Cart
        extendPlanSelected={extendPlanSelected}
        isWallpaper={isWallpaper}
        cartQuantity={cartQuantity}
        setCartQuantity={setCartQuantity}
        handleToggleSideCart={handleToggleSideCart}
        productData={productData}
        price={productData.price}
        isPurchasable={productData.is_purchasable}
      />
      {/* <Policy
        productData={productData}
        categoryId={productData.frame.subcategories[0].category.id}
        frame={productData.frame}
      /> */}
      <ValueProposition
        openByDefault={false}
        className="hide-on-desktop"
        productData={productData}
      />
      <ProductDescription openByDefault={true} productData={productData} />
      <Dimensions
        goToDimensionsImageSlide={goToDimensionsImageSlide}
        openByDefault={true}
        productData={productData}
        isMobile={isMobile}
      />
      <>
        {' '}
        {productData?.fabric && !isColorTreatment ? (
          <FabricDescription openByDefault={true} productData={productData} />
        ) : (
          ''
        )}
      </>
      {!isBedroomSets && (
        <DetailsAndCare
          openByDefault={false}
          productData={productData}
          foundationSlug={foundationSlug}
        />
      )}
      {/* Render when it is a cabana chair / sling or when it is category one or six */}
      {['78', '79', '175', '564'].includes(productData?.frame?.id) && (
        <CompleteTheLook
          source={14}
          title="You May Also Need"
          frameId={productData.frame?.id}
          setChangeFeaturedFabrics={setChangeFeaturedFabrics}
          productSku={productData.sku}
        />
      )}

      {((isRug && !isPad && !isOutdoorRug) ||
        foundationSlug ||
        isCategoryOneOrSix) && (
        <YouMayAlsoNeed
          sizeId={productData?.frame.size?.id}
          slug={foundationSlug}
          source={13}
          productData={productData}
          setChangeFeaturedFabrics={setChangeFeaturedFabrics}
        />
      )}

      {productData &&
        productData.frame &&
        showThePerfectMatchModule(productData.frame.id) && (
          <ThePerfectMatch product={productData} source={22} />
        )}

      {!isMobile && hoveredFabric.image && (
        <HoveredFabric
          hovered={!!hoveredFabric.name.length}
          className="hide-on-mobile"
        >
          <div className="title-fabric-hovered">
            <div className="image-container-hovered">
              <Imgix
                imgixParams={{ auto: 'compress,format' }}
                className="fabric-image"
                sizes="160px"
                src={getImgixUrl(hoveredFabric.image)}
                htmlAttributes={{
                  alt: 'hovered fabric'
                }}
              />
            </div>
            <div className="title-hovered">
              {hoveredFabric.name}{' '}
              {hoveredFabric.collaborator && (
                <span className="collab-name-hovered">
                  {' '}
                  by {hoveredFabric.collaborator.name}
                </span>
              )}
            </div>
          </div>
          {hoveredFabric.description && (
            <div className="description-hovered">
              {hoveredFabric.description}
            </div>
          )}

          {hoveredFabric.material && (
            <div className="details">
              {hoveredFabric.material?.composition && (
                <div className="detail-composition">
                  <div className="detail-composition-title">Composition</div>
                  <div className="detail-composition-desc">
                    {hoveredFabric.material.composition}
                  </div>
                </div>
              )}

              {hoveredFabric.material.durability && (
                <div className="detail-durability">
                  <div className="detail-durability-title">Durability</div>
                  <div className="detail-durability-desc">
                    {hoveredFabric.material.durability}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Pattern ID 15 is for rug "fabrics" that are not made to order */}
          {hoveredFabric.pattern?.id && hoveredFabric.pattern?.id != 15 && (
            <div className="pattern-info">
              {hoveredFabric.pattern?.id == 3
                ? 'Please note each piece is made to order. Color may vary slightly from image.'
                : 'Please note each piece is made to order. Pattern placement and color may vary slightly from image.'}
            </div>
          )}
        </HoveredFabric>
      )}
      <GlobalStyle open={inView} />
    </Wrapper>
  );
}

export default Sidebar;

function getNonQuickShipOptionFabricId(getFeaturedProductFabrics, productData) {
  return getFeaturedProductFabrics.find(
    fabric => !isFabricQuickShip(fabric, productData)
  ).id;
}

function hasFabrics(productData, featuredFabrics) {
  return (
    productData.fabric &&
    featuredFabrics &&
    featuredFabrics?.getFeaturedProductFabrics?.length > 1
  );
}

const HoveredFabric = styled.div`
  display: ${({ hovered }) => (hovered ? 'block' : 'none')};
  position: absolute;
  top: 310px;
  right: 443px;
  font-family: ${({ theme }) => theme.fonts.primary};
  background: white;
  width: 400px;
  padding: 20px;
  text-align: start;
  box-shadow: 0 0 1px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
    0 4px 8px rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  border: 1px solid #ebebeb;

  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;

  .pattern-info {
    margin-top: 20px;
  }

  .title-fabric-hovered {
    display: grid;
    grid-template-columns: 150px auto;
    grid-gap: 10px;
    .title-hovered {
      font-family: ${({ theme }) => theme.fonts.secondary.family};
      letter-spacing: ${({ theme }) => theme.fonts.secondary.letterSpacing};
      font-style: normal;
      font-weight: 600;
      font-size: 20px;
      line-height: 24px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;

      .collab-name-hovered {
        font-family: ${({ theme }) => theme.fonts.primary};
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        line-height: 17px;
      }
    }
    .image-container-hovered {
      margin-right: 15px;
      width: 140px;
      height: 140px;
      overflow: hidden;
      img {
        transform: scale(1.2);
        width: auto;
        height: 100%;
      }
    }
  }

  .description-hovered {
    margin: 20px 0;
  }

  .details {
    margin-top: 20px;
    display: grid;
    grid-template-columns: 1fr 1fr;

    .detail-composition-title,
    .detail-durability-title {
      font-weight: bold;
      font-size: 14px;
      line-height: 17px;
    }
    .detail-composition,
    .detail-durability {
      align-self: start;
    }
  }
`;
